import React from 'react';
import { Link } from 'react-router-dom';
import PublicHeader from './PublicHeader';
import { rexTechTheme } from '../Theme';
import { 
  Recycle, 
  Shield, 
  Globe, 
  Leaf, 
  Truck, 
  Factory,
  Users,
  Award,
  TrendingUp,
  Zap,
  Clock,
  CheckCircle,
  ChevronRight
} from 'lucide-react';

const Home = () => {
  const features = [
    { 
      icon: <Recycle size={32} />, 
      title: 'Material Collection', 
      description: 'Efficient collection of recyclable materials from various sources' 
    },
    { 
      icon: <Factory size={32} />, 
      title: 'Advanced Recycling', 
      description: 'State-of-the-art recycling facilities for maximum material recovery' 
    },
    { 
      icon: <Shield size={32} />, 
      title: 'Ozone Protection', 
      description: 'Specialized processes to prevent ozone-depleting substances' 
    },
    { 
      icon: <Leaf size={32} />, 
      title: 'Sustainable Solutions', 
      description: 'Eco-friendly approaches for a better tomorrow' 
    },
  ];

  const stats = [
    { value: '50K+', label: 'Materials Collected', icon: <Truck />, color: rexTechTheme.colors.primary.main },
    { value: '99%', label: 'Recycling Rate', icon: <Recycle />, color: rexTechTheme.colors.accent.teal },
    { value: '100+', label: 'Cities Served', icon: <Globe />, color: rexTechTheme.colors.secondary.main },
    { value: '24/7', label: 'Operation', icon: <Clock />, color: rexTechTheme.colors.accent.orange },
  ];

  const handleJoinMission = () => {
    window.location.href = '/register';
  };

  const handleLearnMore = () => {
    const missionSection = document.getElementById('mission');
    if (missionSection) {
      missionSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div style={{ fontFamily: rexTechTheme.typography.fontFamily }}>
      <PublicHeader />
      
      {/* Hero Section */}
      <section style={{
        background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.dark} 0%, ${rexTechTheme.colors.primary.main} 100%)`,
        color: rexTechTheme.colors.neutral.white,
        padding: `${rexTechTheme.spacing.xxl} ${rexTechTheme.spacing.lg}`,
        textAlign: 'center'
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <h1 style={{
            fontSize: rexTechTheme.typography.fontSize.xxxlarge,
            fontWeight: rexTechTheme.typography.fontWeight.bold,
            marginBottom: rexTechTheme.spacing.md,
            lineHeight: 1.2
          }}>
            Creating a <span style={{
              background: `linear-gradient(45deg, ${rexTechTheme.colors.primary.lighter}, ${rexTechTheme.colors.accent.yellow})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>New Home</span> for Our Planet
          </h1>
          <p style={{
            fontSize: rexTechTheme.typography.fontSize.large,
            marginBottom: rexTechTheme.spacing.xl,
            opacity: 0.9,
            lineHeight: 1.6
          }}>
            At RexTech, we transform waste into opportunities while protecting our ozone layer. 
            Join us in building a sustainable future for generations to come.
          </p>
          <div style={{
            display: 'flex',
            gap: rexTechTheme.spacing.md,
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button 
              onClick={handleJoinMission}
              style={{
                padding: `${rexTechTheme.spacing.lg} ${rexTechTheme.spacing.xxl}`,
                background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.lighter} 0%, ${rexTechTheme.colors.primary.light} 100%)`,
                color: rexTechTheme.colors.primary.dark,
                border: 'none',
                borderRadius: rexTechTheme.borderRadius.large,
                fontSize: rexTechTheme.typography.fontSize.large,
                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: rexTechTheme.spacing.sm,
                boxShadow: '0 4px 15px rgba(88, 214, 141, 0.3)',
                ':hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 6px 20px rgba(88, 214, 141, 0.4)',
                  background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.light} 0%, ${rexTechTheme.colors.primary.lighter} 100%)`
                }
              }}
            >
              Join Our Mission
              <ChevronRight size={20} />
            </button>
            <button 
              onClick={handleLearnMore}
              style={{
                padding: `${rexTechTheme.spacing.lg} ${rexTechTheme.spacing.xxl}`,
                backgroundColor: 'transparent',
                color: rexTechTheme.colors.neutral.white,
                border: `2px solid ${rexTechTheme.colors.primary.lighter}`,
                borderRadius: rexTechTheme.borderRadius.large,
                fontSize: rexTechTheme.typography.fontSize.large,
                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                gap: rexTechTheme.spacing.sm,
                ':hover': {
                  backgroundColor: rexTechTheme.colors.primary.lighter,
                  color: rexTechTheme.colors.primary.dark,
                  transform: 'translateY(-4px)'
                }
              }}
            >
              Learn More
            </button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section style={{
        padding: `${rexTechTheme.spacing.xxl} ${rexTechTheme.spacing.lg}`,
        backgroundColor: rexTechTheme.colors.neutral.white
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: rexTechTheme.spacing.lg
        }}>
          {stats.map((stat, index) => (
            <div key={index} style={{
              textAlign: 'center',
              padding: rexTechTheme.spacing.xl,
              borderRadius: rexTechTheme.borderRadius.large,
              backgroundColor: rexTechTheme.colors.neutral.lightGray,
              border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
              transition: 'transform 0.3s ease',
              position: 'relative',
              overflow: 'hidden',
              ':hover': {
                transform: 'translateY(-8px)',
                boxShadow: rexTechTheme.shadows.medium,
                borderColor: stat.color
              }
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: `linear-gradient(90deg, ${stat.color} 0%, ${rexTechTheme.colors.primary.light} 100%)`
              }} />
              
              <div style={{
                color: stat.color,
                marginBottom: rexTechTheme.spacing.lg,
                display: 'flex',
                justifyContent: 'center'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  backgroundColor: `${stat.color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {React.cloneElement(stat.icon, { size: 28 })}
                </div>
              </div>
              
              <h3 style={{
                fontSize: rexTechTheme.typography.fontSize.xxlarge,
                color: rexTechTheme.colors.primary.dark,
                margin: `0 0 ${rexTechTheme.spacing.xs} 0`,
                fontWeight: rexTechTheme.typography.fontWeight.bold
              }}>
                {stat.value}
              </h3>
              
              <p style={{
                color: rexTechTheme.colors.neutral.darkGray,
                margin: 0,
                fontSize: rexTechTheme.typography.fontSize.regular,
                fontWeight: rexTechTheme.typography.fontWeight.medium
              }}>
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section style={{
        padding: `${rexTechTheme.spacing.xxl} ${rexTechTheme.spacing.lg}`,
        backgroundColor: rexTechTheme.colors.neutral.lightGray
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: rexTechTheme.typography.fontSize.xxlarge,
            color: rexTechTheme.colors.primary.dark,
            marginBottom: rexTechTheme.spacing.xl,
            fontWeight: rexTechTheme.typography.fontWeight.bold
          }}>
            Our Core Initiatives
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: rexTechTheme.spacing.lg
          }}>
            {features.map((feature, index) => (
              <div key={index} style={{
                padding: rexTechTheme.spacing.xl,
                backgroundColor: rexTechTheme.colors.neutral.white,
                borderRadius: rexTechTheme.borderRadius.large,
                border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                ':hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: rexTechTheme.shadows.medium,
                  borderColor: rexTechTheme.colors.primary.light
                }
              }}>
                <div style={{
                  color: rexTechTheme.colors.primary.main,
                  marginBottom: rexTechTheme.spacing.lg,
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  <div style={{
                    width: '70px',
                    height: '70px',
                    borderRadius: '50%',
                    backgroundColor: `${rexTechTheme.colors.primary.main}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {React.cloneElement(feature.icon, { size: 32 })}
                  </div>
                </div>
                
                <h3 style={{
                  fontSize: rexTechTheme.typography.fontSize.xlarge,
                  color: rexTechTheme.colors.primary.dark,
                  marginBottom: rexTechTheme.spacing.md,
                  fontWeight: rexTechTheme.typography.fontWeight.semiBold
                }}>
                  {feature.title}
                </h3>
                
                <p style={{
                  color: rexTechTheme.colors.neutral.darkGray,
                  lineHeight: 1.7,
                  flex: 1
                }}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section id="mission" style={{
        padding: `${rexTechTheme.spacing.xxl} ${rexTechTheme.spacing.lg}`,
        background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.main} 0%, ${rexTechTheme.colors.primary.dark} 100%)`,
        color: rexTechTheme.colors.neutral.white,
        textAlign: 'center'
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <div style={{
            width: '100px',
            height: '100px',
            margin: '0 auto',
            marginBottom: rexTechTheme.spacing.xl,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(10px)'
          }}>
            <Shield size={48} color={rexTechTheme.colors.primary.lighter} />
          </div>
          
          <h2 style={{
            fontSize: rexTechTheme.typography.fontSize.xxlarge,
            marginBottom: rexTechTheme.spacing.lg,
            fontWeight: rexTechTheme.typography.fontWeight.bold,
            lineHeight: 1.3
          }}>
            Our Mission: Protect & Preserve
          </h2>
          
          <p style={{
            fontSize: rexTechTheme.typography.fontSize.large,
            lineHeight: 1.8,
            marginBottom: rexTechTheme.spacing.xxl,
            opacity: 0.9
          }}>
            At RexTech, we believe in creating a new home for our planet by responsibly 
            collecting materials, implementing advanced recycling technologies, and 
            actively protecting the ozone layer. Every action we take is a step toward 
            a sustainable future.
          </p>
          
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: rexTechTheme.spacing.lg,
            flexWrap: 'wrap'
          }}>
            <div style={{ 
              textAlign: 'center',
              padding: rexTechTheme.spacing.lg,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: rexTechTheme.borderRadius.large,
              backdropFilter: 'blur(10px)',
              minWidth: '180px'
            }}>
              <div style={{
                fontSize: rexTechTheme.typography.fontSize.xxlarge,
                fontWeight: 'bold',
                color: rexTechTheme.colors.primary.lighter,
                marginBottom: rexTechTheme.spacing.xs
              }}>
                2024
              </div>
              <div style={{ fontSize: rexTechTheme.typography.fontSize.small }}>
                Year of Operation
              </div>
            </div>
            
            <div style={{ 
              textAlign: 'center',
              padding: rexTechTheme.spacing.lg,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: rexTechTheme.borderRadius.large,
              backdropFilter: 'blur(10px)',
              minWidth: '180px'
            }}>
              <div style={{
                fontSize: rexTechTheme.typography.fontSize.xxlarge,
                fontWeight: 'bold',
                color: rexTechTheme.colors.primary.lighter,
                marginBottom: rexTechTheme.spacing.xs
              }}>
                500+
              </div>
              <div style={{ fontSize: rexTechTheme.typography.fontSize.small }}>
                Environmental Projects
              </div>
            </div>
            
            <div style={{ 
              textAlign: 'center',
              padding: rexTechTheme.spacing.lg,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: rexTechTheme.borderRadius.large,
              backdropFilter: 'blur(10px)',
              minWidth: '180px'
            }}>
              <div style={{
                fontSize: rexTechTheme.typography.fontSize.xxlarge,
                fontWeight: 'bold',
                color: rexTechTheme.colors.primary.lighter,
                marginBottom: rexTechTheme.spacing.xs
              }}>
                1M+
              </div>
              <div style={{ fontSize: rexTechTheme.typography.fontSize.small }}>
                Tons of CO₂ Reduced
              </div>
            </div>
          </div>
        </div>
      </section>

     

      {/* Footer - Same as Login/Register */}
      <footer style={{
        backgroundColor: rexTechTheme.colors.neutral.white,
        padding: rexTechTheme.spacing.xl,
        borderTop: `1px solid ${rexTechTheme.colors.neutral.gray}`,
        textAlign: 'center'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          color: rexTechTheme.colors.neutral.darkGray,
          fontSize: rexTechTheme.typography.fontSize.small
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: rexTechTheme.spacing.md,
            marginBottom: rexTechTheme.spacing.lg
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: rexTechTheme.spacing.md,
              marginBottom: rexTechTheme.spacing.sm
            }}>
             
            </div>
            
            <p style={{
              fontSize: rexTechTheme.typography.fontSize.large,
              textAlign: 'center',
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: 1.6,
              color: rexTechTheme.colors.neutral.black
            }}>
              "Creating a New Home for Our Planet"
            </p>
          </div>
          
          <p style={{ margin: 0 }}>
            © 2024 RexTech. Creating a new home for our planet through sustainable technology.
          </p>
          
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: rexTechTheme.spacing.lg,
            marginTop: rexTechTheme.spacing.md,
            flexWrap: 'wrap'
          }}>
            <Link 
              to="/privacy" 
              style={{
                color: rexTechTheme.colors.neutral.darkGray,
                textDecoration: 'none',
                ':hover': {
                  textDecoration: 'underline',
                  color: rexTechTheme.colors.primary.main
                }
              }}
            >
              Privacy Policy
            </Link>
            
            <Link 
              to="/terms" 
              style={{
                color: rexTechTheme.colors.neutral.darkGray,
                textDecoration: 'none',
                ':hover': {
                  textDecoration: 'underline',
                  color: rexTechTheme.colors.primary.main
                }
              }}
            >
              Terms of Service
            </Link>
            
            <Link 
              to="/contact" 
              style={{
                color: rexTechTheme.colors.neutral.darkGray,
                textDecoration: 'none',
                ':hover': {
                  textDecoration: 'underline',
                  color: rexTechTheme.colors.primary.main
                }
              }}
            >
              Contact Support
            </Link>
            
            <Link 
              to="/sustainability" 
              style={{
                color: rexTechTheme.colors.neutral.darkGray,
                textDecoration: 'none',
                ':hover': {
                  textDecoration: 'underline',
                  color: rexTechTheme.colors.primary.main
                }
              }}
            >
              Sustainability Report
            </Link>
          </div>
          
          <div style={{
            marginTop: rexTechTheme.spacing.lg,
            paddingTop: rexTechTheme.spacing.md,
            borderTop: `1px solid ${rexTechTheme.colors.neutral.gray}`,
            fontSize: rexTechTheme.typography.fontSize.xs,
            color: rexTechTheme.colors.neutral.darkGray
          }}>
            <p style={{ margin: 0 }}>
              RexTech is committed to environmental protection and sustainable development.
              All our processes are designed to minimize ecological impact.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;